import { QuestConfig, Token } from "@devour/client";
import getConfig from "@/utils/getConfig";
import { useQuery } from "@tanstack/react-query";

interface GetAllQuestConfigsForUserRequest {
    sort?: "createdAt" | "name";
    order?: "asc" | "desc";
    limit?: number;
    offset?: number;
}

interface GetAllQuestConfigsForUserResponse {
    questConfigs: Array<QuestConfig>;
    paginationInfo: {
        currentPage: number;
        totalPages: number;
        totalRecords: number;
        recordsPerPage: number;
        hasNext: boolean;
        hasPrevious: boolean;
    };
}

async function fetchAllQuestConfigsForUser(
    fullToken: Token,
    query: GetAllQuestConfigsForUserRequest = {},
): Promise<Array<QuestConfig>> {
    const config = getConfig(fullToken);
    const params = new URLSearchParams();

    // Set default pagination parameters if not provided
    params.set("limit", (query.limit || 50).toString());
    params.set("offset", (query.offset || 0).toString());

    if (query.sort) params.set("sort", query.sort);
    if (query.order) params.set("order", query.order);

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        // Use headers from getConfig which properly handles authorization
        ...config.headers,
    };

    console.log("Making API request with config:", {
        basePath: config.basePath,
        hasAuthHeader: Boolean(headers.authorization),
        authPreview: headers.authorization ? headers.authorization.substring(0, 30) + "..." : "none"
    });

    const response = await fetch(`${config.basePath}/quest-configs/user/all?${params}`, {
        headers,
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error("All quest configs API error:", {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
        });
        throw new Error(`Failed to fetch all quest configs: ${response.status} - ${errorText}`);
    }

    const data: GetAllQuestConfigsForUserResponse = await response.json();
    return data.questConfigs;
}

function getAllQuestConfigsForUserQuery(fullToken: Token, query: GetAllQuestConfigsForUserRequest = {}) {
    return {
        queryKey: [
            "quest-configs-user-all",
            fullToken?.id ?? "unauthenticated",
            query,
        ],
        queryFn: () => fetchAllQuestConfigsForUser(fullToken, query),
        keepPreviousData: true,
        staleTime: 30 * 24 * 60 * 60000,
        refetchOnWindowFocus: false,
    } as const;
}

export function useGetAllQuestConfigsForUser(fullToken: Token, query?: GetAllQuestConfigsForUserRequest) {
    console.log("🚀 INSIDE useGetAllQuestConfigsForUser - THIS SHOULD ALWAYS APPEAR!", {
        hasToken: Boolean(fullToken?.token),
        queryDefined: query !== undefined,
        query
    });


    const baseQuery = getAllQuestConfigsForUserQuery(fullToken, query || {});
    const enabled = Boolean(fullToken?.token && query !== undefined);

    console.log("🔥 HOOK ENABLED STATUS - THIS SHOULD ALSO APPEAR:", enabled);

    return useQuery({
        ...baseQuery,
        enabled,
    });
}